<template>
  <div
    v-editable="blok"
    class="relative h-fill-available flex flex-col overflow-hidden"
    :class="{
      'justify-center': blok.textPosition === 'center',
      'justify-end':
        blok.textPosition === 'right' || blok.textPosition === 'left',
    }"
    :data-many-scroller="progress">
    <ElementPattern
      v-if="blok.backgroundPattern?.length && blok.backgroundMedia"
      :bloks="blok.backgroundPattern"
      :show="true"
      :fadeOut="false"
      objectFit="cover"
      position="bottom-center"
      className="absolute top-0 left-0 w-full h-full">
      <ElementMedia
        :bloks="blok.backgroundMedia"
        :ratio="imgRatio"
        class="w-full h-full object-cover media-scroll-parallax"
        :sizes="[{ vw: 100, bpMin: '0' }]"
        :autoplay="true"
        :controls="false"
        :loop="true" />
      <AGradientBlur
        class="absolute left-0 top-0 h-full w-full"
        :direction="gradientDirection"
        :grayscale="true"
        :strength="
          blok?.tintOpacity?.value !== undefined &&
          blok?.tintOpacity?.value !== null
            ? blok.tintOpacity.value
            : 0.75
        "
      />
    </ElementPattern>
    <template v-else-if="blok.backgroundMedia">
      <ElementMedia
        :bloks="blok.backgroundMedia"
        :ratio="imgRatio"
        class="absolute top-0 left-0 w-full h-full object-cover media-scroll-parallax"
        :sizes="[{ vw: 100, bpMin: '0' }]"
        :autoplay="true"
        :controls="false"
        :loop="true" />
      <AGradientBlur
        v-if="blok.backgroundMedia"
        class="absolute left-0 h-full w-full"
        :direction="gradientDirection"
        :grayscale="true"
        :strength="
          blok?.tintOpacity?.value !== undefined &&
          blok?.tintOpacity?.value !== null
            ? blok.tintOpacity.value
            : 0.75
        "
      />
    </template>
    <div
      class="relative page-mx clamp-py-18-24 text-white feature-text"
      v-if="blok.texts?.length">
      <ElementTexts :bloks="blok.texts" :textPosition="blok.textPosition" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ModuleFeatureStoryblok } from "@/types";
import useWindowAspect from "@/composables/useWindowAspect";
import { useManyScrollerOptions } from "@/composables/useManyScrollerOptions";
const { progress } = useManyScrollerOptions();

interface Props {
  blok: ModuleFeatureStoryblok;
}
const props = defineProps<Props>();

const { aspect } = useWindowAspect();

const imgRatio = computed(() => {
  if (aspect.value > 0.8 && aspect.value < 1.2) return { w: 1, h: 1 };
  else if (aspect.value >= 1.2) return { w: 16, h: 9 };
  else return { w: 9, h: 12 };
});

const gradientDirection = computed(() => {
  switch (props.blok.textPosition) {
    case "left":
      return "to top right";
    case "right":
      return "to top left";
    default:
      return "fill";
  }
});
</script>

<style lang="scss">
.media-scroll-parallax {
  transition: transform 250ms ease-out;
  transform: scale(1.2) translateY(calc(var(--scroll-position-full, 0) * 20%));
}
</style>

<style lang="scss" scoped>
.feature-text {
  :deep(.rich-text) {
    @apply text-max-w-feature;
  }
}
</style>
